import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Link,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiPlus,
} from "react-icons/fi";
import {
  FaFacebook,
  FaLine,
  FaSms,
  FaTelegramPlane,
  FaWeixin,
  FaWhatsapp,
} from "react-icons/fa";

import attractions from "./attractions.json";
import { ATTRACTIONS } from "./Hero";

const DEFAULT_TABS = [
  { id: "description", label: "Description" },
  { id: "thingsToNote", label: "Things to Note" },
  { id: "operatingHours", label: "Operating Hours" },
  { id: "location", label: "Location" },
];

function readStored(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function safeUrl(value) {
  if (typeof value !== "string") return null;

  try {
    const url = new URL(value, window.location.origin);

    return ["http:", "https:", "mailto:", "tel:"].includes(url.protocol)
      ? url.href
      : null;
  } catch {
    return null;
  }
}

function normalizeTab(id) {
  if (id === "notes") return "thingsToNote";
  if (id === "hours") return "operatingHours";
  return id;
}

/* Выделения и ссылки внутри обычного текста, без HTML. */
function RichText({ value }) {
  const data =
    typeof value === "string" ? { text: value } : value || {};

  const text = data.text || "";
  const marks = [];

  function addPhrases(phrases, property) {
    const values = Array.isArray(phrases)
      ? phrases
      : phrases
        ? [phrases]
        : [];

    values.forEach((phrase) => {
      if (typeof phrase !== "string" || !phrase) return;

      let start = text.indexOf(phrase);

      while (start !== -1) {
        marks.push({
          start,
          end: start + phrase.length,
          [property]: true,
        });

        start = text.indexOf(phrase, start + phrase.length);
      }
    });
  }

  addPhrases(data.boldPhrases, "bold");
  addPhrases(data.redPhrases, "red");
  addPhrases(data.italicPhrases, "italic");
  addPhrases(data.underline, "underline");

  (data.links || []).forEach((link) => {
    const href = safeUrl(link.href);
    if (!href || !link.text) return;

    let start = text.indexOf(link.text);

    while (start !== -1) {
      marks.push({
        start,
        end: start + link.text.length,
        href,
      });

      start = text.indexOf(link.text, start + link.text.length);
    }
  });

  const boundaries = [
    ...new Set([
      0,
      text.length,
      ...marks.flatMap((mark) => [mark.start, mark.end]),
    ]),
  ].sort((a, b) => a - b);

  return boundaries.slice(0, -1).map((start, index) => {
    const end = boundaries[index + 1];
    const active = marks.filter(
      (mark) => mark.start <= start && mark.end >= end
    );

    const className = [
      active.some((mark) => mark.bold) ? "font-bold" : "",
      active.some((mark) => mark.red) ? "text-red-500" : "",
      active.some((mark) => mark.italic) ? "italic" : "",
      active.some((mark) => mark.underline) ? "underline" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const link = active.find((mark) => mark.href);
    const content = text.slice(start, end);

    return link ? (
      <a
        key={start}
        href={link.href}
        target={/^https?:/.test(link.href) ? "_blank" : undefined}
        rel="noopener noreferrer"
        className={`break-words text-teal-500 hover:underline ${className}`}
      >
        {content}
      </a>
    ) : (
      <span key={start} className={className || undefined}>
        {content}
      </span>
    );
  });
}

function ContentList({ items = [], bold = false, nested = false }) {
  return (
    <ul
      className={`pl-5 ${
        nested ? "mt-3 list-disc space-y-3" : "list-disc"
      } ${bold ? "font-semibold" : ""}`}
    >
      {items.map((item, index) => {
        const data = typeof item === "string" ? { text: item } : item;

        return (
          <li
            key={index}
            className={[
              data.color === "red" ? "text-red-500" : "",
              data.bold ? "font-semibold" : "",
              data.children?.length ? "mb-3" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <RichText value={data} />

            {data.children?.length > 0 && (
              <div className="font-normal">
                <ContentList items={data.children} nested />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ContentBlocks({ blocks = [] }) {
  return (
    <div className="space-y-4 text-xs leading-relaxed lg:text-sm">
      {blocks.map((block, index) => {
        switch (block.type) {
          case "heading":
            return (
              <div key={index}>
                <h2 className="text-lg font-bold leading-tight lg:text-xl">
                  {block.text}
                </h2>
                <div className="mt-1.5 h-1 w-10 rounded-full bg-teal-500" />
              </div>
            );

          case "paragraph":
          case "notice":
            return (
              <p
                key={index}
                className={[
                  "whitespace-pre-line",
                  block.bold ? "font-semibold" : "",
                  block.type === "notice"
                    ? "font-semibold text-red-500"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <RichText value={block} />
              </p>
            );

          case "list":
            return (
              <ContentList
                key={index}
                items={block.items}
                bold={block.bold}
              />
            );

          case "divider":
            return (
              <div key={index} className="py-2">
                <hr className="border-neutral-200" />
              </div>
            );

          case "table":
            return (
              <div key={index} className="overflow-x-auto">
                <table
                  className={`text-left ${
                    block.bold ? "font-semibold" : ""
                  }`}
                >
                  <tbody>
                    {(block.rows || []).map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) =>
                          cellIndex === 0 ? (
                            <th
                              key={cellIndex}
                              scope="row"
                              className="py-0.5 pr-10 font-inherit"
                            >
                              <RichText value={cell} />
                            </th>
                          ) : (
                            <td key={cellIndex} className="py-0.5 pr-4">
                              <RichText value={cell} />
                            </td>
                          )
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );

          case "link": {
            const href = safeUrl(block.href);

            return href ? (
              <a
                key={index}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="block break-words text-teal-500 hover:underline"
              >
                {block.text}
              </a>
            ) : null;
          }

          case "map": {
            const src = safeUrl(block.src);

            if (!src || !/^https?:/.test(src)) return null;

            return (
              <iframe
                key={index}
                title={block.title || "Location"}
                src={src}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                className="h-64 w-full border-0 lg:h-80"
              />
            );
          }

          default:
            return null;
        }
      })}
    </div>
  );
}

function Gallery({ images, title }) {
  const [active, setActive] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const openerRef = useRef(null);
  const closeRef = useRef(null);
  const dialogRef = useRef(null);

  function move(direction) {
    setActive(
      (current) =>
        (current + direction + images.length) % images.length
    );
  }

  useEffect(() => {
    if (expanded || images.length < 2) return;

    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % images.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [expanded, images.length, active]);

  useEffect(() => {
    if (!expanded) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    function onKeyDown(event) {
      if (event.key === "Escape") setExpanded(false);

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setActive(
          (current) => (current - 1 + images.length) % images.length
        );
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        setActive((current) => (current + 1) % images.length);
      }

      if (event.key === "Tab") {
        const buttons = Array.from(
          dialogRef.current?.querySelectorAll("button") || []
        );

        const first = buttons[0];
        const last = buttons[buttons.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (
          !event.shiftKey &&
          document.activeElement === last
        ) {
          event.preventDefault();
          first?.focus();
        }
      }
    }

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      openerRef.current?.focus();
    };
  }, [expanded, images.length]);

  function renderArrows(large = false) {
    if (images.length < 2) return null;

    return [
      {
        label: "Previous image",
        direction: -1,
        position: "left-3",
        Icon: FiChevronLeft,
      },
      {
        label: "Next image",
        direction: 1,
        position: "right-3",
        Icon: FiChevronRight,
      },
    ].map(({ label, direction, position, Icon }) => (
      <button
        key={label}
        type="button"
        aria-label={label}
        onClick={() => move(direction)}
        className={`absolute top-1/2 z-20 flex -translate-y-1/2 items-center justify-center rounded-full bg-white/60 text-neutral-600 transition-colors hover:bg-white ${position} ${
          large ? "h-12 w-12" : "h-9 w-9 lg:h-10 lg:w-10"
        }`}
      >
        <Icon className="h-6 w-6" aria-hidden="true" />
      </button>
    ));
  }

  return (
    <>
      <div className="relative isolate h-56 overflow-hidden bg-neutral-900 lg:h-80">
        {images.map((src, index) => (
          <div
            key={`background-${index}`}
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 transition-opacity duration-700 motion-reduce:transition-none ${
              active === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={src}
              alt=""
              className="h-full w-full scale-110 object-cover blur-sm brightness-50"
            />
          </div>
        ))}

        <button
          ref={openerRef}
          type="button"
          aria-label={`Enlarge ${title} image`}
          onClick={() => setExpanded(true)}
          className="absolute inset-0 z-10 cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500"
        >
          {images.map((src, index) => (
            <img
              key={index}
              src={src}
              alt={active === index ? title : ""}
              aria-hidden={active !== index}
              className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-700 motion-reduce:transition-none ${
                active === index ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
        </button>

        {renderArrows()}
      </div>

      <div className="flex gap-2 overflow-x-auto bg-neutral-50 p-3 pb-4">
        {images.map((src, index) => (
          <button
            key={index}
            type="button"
            aria-label={`Show image ${index + 1}`}
            aria-pressed={active === index}
            onClick={() => setActive(index)}
            className="flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
          >
            <img
              src={src}
              alt=""
              className="h-full w-full object-contain"
            />
          </button>
        ))}
      </div>

      {expanded &&
        createPortal(
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={`${title} gallery`}
            onClick={() => setExpanded(false)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 lg:p-12"
          >
            <button
              ref={closeRef}
              type="button"
              aria-label="Close gallery"
              onClick={() => setExpanded(false)}
              className="absolute right-4 top-4 z-30 rounded-full bg-white/20 p-2 text-white hover:bg-white/30"
            >
              <FiX className="h-6 w-6" aria-hidden="true" />
            </button>

            <div
              onClick={(event) => event.stopPropagation()}
              className="relative h-full w-full"
            >
              {images.map((src, index) => (
                <img
                  key={index}
                  src={src}
                  alt={active === index ? title : ""}
                  aria-hidden={active !== index}
                  className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-700 motion-reduce:transition-none ${
                    active === index ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}

              {renderArrows(true)}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

function ShareButtons({ title }) {
  const [message, setMessage] = useState("");
  const url = new URL(window.location.href);

  // Ссылка сохраняет выбранную панель при отправке.
  const encodedUrl = encodeURIComponent(url.href);
  const encodedText = encodeURIComponent(`${title} ${url.href}`);

  const links = [
    {
      label: "SMS",
      Icon: FaSms,
      href: `sms:?body=${encodedText}`,
      color: "bg-lime-500",
    },
    {
      label: "LINE",
      Icon: FaLine,
      href: `https://social-plugins.line.me/lineit/share?url=${encodedUrl}`,
      color: "bg-green-500",
    },
    {
      label: "Facebook",
      Icon: FaFacebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "bg-blue-600",
    },
    {
      label: "WeChat: copy link",
      Icon: FaWeixin,
      color: "bg-lime-600",
    },
    {
      label: "Telegram",
      Icon: FaTelegramPlane,
      href: `https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(title)}`,
      color: "bg-sky-500",
    },
    {
      label: "WhatsApp",
      Icon: FaWhatsapp,
      href: `https://wa.me/?text=${encodedText}`,
      color: "bg-green-600",
    },
  ];

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url.href);
      setMessage("Link copied");
    } catch {
      setMessage("Unable to copy link");
    }
  }

  async function share() {
    if (!navigator.share) {
      await copyLink();
      return;
    }

    try {
      await navigator.share({ title, url: url.href });
    } catch (error) {
      if (error.name !== "AbortError") await copyLink();
    }
  }

  return (
    <div className="relative flex flex-wrap justify-end gap-1">
      {links.map(({ label, Icon, href, color }) => {
        const className =
          `flex h-7 w-7 items-center justify-center rounded text-white ${color}`;

        return href ? (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share via ${label}`}
            className={className}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
          </a>
        ) : (
          <button
            key={label}
            type="button"
            onClick={copyLink}
            aria-label={label}
            className={className}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
          </button>
        );
      })}

      <button
        type="button"
        onClick={share}
        aria-label="More sharing options"
        className="flex h-7 w-7 items-center justify-center rounded bg-blue-600 text-white"
      >
        <FiPlus className="h-6 w-6" aria-hidden="true" />
      </button>

      <span
        role="status"
        className="absolute right-0 top-full mt-1 text-xs text-neutral-500"
      >
        {message}
      </span>
    </div>
  );
}

function AttractionDetails({ attraction, content, images }) {
  const [activeTab, setActiveTab] = useState("description");

  const tabs = (content.tabs || DEFAULT_TABS).map((tab) => ({
    ...tab,
    id: normalizeTab(tab.id),
  }));

  const selectedTab = tabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : tabs[0]?.id;

  const blocks =
    selectedTab === "location"
      ? attractions["standard-visit"]?.location || []
      : content[selectedTab] || [];

  function handleTabKey(event, currentIndex) {
    let nextIndex;

    switch (event.key) {
      case "ArrowRight":
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case "ArrowLeft":
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextId = tabs[nextIndex].id;
    setActiveTab(nextId);
    document.getElementById(`tab-${nextId}`)?.focus();
  }

  return (
    <>
      <nav
        aria-label="Breadcrumb"
        className="mb-3 flex items-start gap-1.5 px-3 text-xs lg:text-sm"
      >
        <Link to="/" className="shrink-0 text-teal-500">
          Home
        </Link>

        <FiChevronRight
          aria-hidden="true"
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-neutral-500"
        />

        <span className="min-w-0 break-words">
          {attraction.title}
        </span>
      </nav>

      <Gallery images={images} title={attraction.title} />

      <div className="px-3 pt-6">
        <p className="text-xs lg:text-sm">
          PETRONAS Twin Towers Sky Xperience
        </p>

        <h1 className="mt-1 break-words text-xl font-bold leading-tight lg:text-2xl">
          {attraction.title}
        </h1>

        <div className="mt-2">
          <ShareButtons title={attraction.title} />
        </div>

        <div
          role="tablist"
          aria-label="Attraction information"
          className="mt-5 flex flex-wrap gap-1"
        >
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              id={`tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={selectedTab === tab.id}
              aria-controls={`panel-${tab.id}`}
              tabIndex={selectedTab === tab.id ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(event) => handleTabKey(event, index)}
              className={`min-w-0 flex-1 rounded px-3 py-2 text-xs shadow-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 lg:w-48 lg:flex-none lg:text-sm ${
                selectedTab === tab.id
                  ? "bg-teal-500 font-semibold text-white"
                  : "bg-white text-teal-500 hover:bg-teal-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <section
          id={`panel-${selectedTab}`}
          role="tabpanel"
          aria-labelledby={`tab-${selectedTab}`}
          tabIndex={0}
          className="px-3 pt-4 focus-visible:outline-teal-500"
        >
          <ContentBlocks blocks={blocks} />
        </section>
      </div>
    </>
  );
}

export default function HardPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [storedSelection] = useState(
    () => readStored("selectedAttraction")
  );

  const [storedBooking] = useState(
    () => readStored("visitBooking")
  );

  const incoming =
    location.state?.selectedAttraction ||
    location.state?.booking?.attraction ||
    storedSelection ||
    storedBooking?.attraction;

  const requestedId =
    searchParams.get("attraction") || incoming?.id;

  const attraction = ATTRACTIONS.find(
    (item) => item.id === requestedId
  );

  const content = attractions[requestedId];

  const galleryByAttraction = {
    "standard-visit": ["/p1.png"],
    "evening-xperience": [
      "/p2.png",
      "/r2.png",
      "/r3.png",
      "/r4.png",
      "/r5.png",
      "/r6.png",
      "/r7.png",
    ],
    "priority-access": ["/p3.png"],
    "hop-on-hop-off": ["/p4.png"],
  };

  const images = attraction
    ? galleryByAttraction[attraction.id] || [attraction.image]
    : [];

  useEffect(() => {
    if (!attraction || searchParams.get("attraction")) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("attraction", attraction.id);
    setSearchParams(nextParams, { replace: true });
  }, [attraction, searchParams, setSearchParams]);

  if (!attraction || !content) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 font-sans">
        <p className="text-sm font-semibold">
          {!attraction
            ? "Please select an attraction."
            : "Description for this attraction has not been added yet."}
        </p>

        <Link
          to="/"
          className="mt-3 inline-block text-sm text-teal-500 hover:underline"
        >
          Back to attractions
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-4 pb-9 pt-4 font-sans text-black">
      <AttractionDetails
        key={attraction.id}
        attraction={attraction}
        content={content}
        images={images}
      />
    </main>
  );
}